import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { Plus, Loader2, X, Mic2, Trash2, Edit, ExternalLink, Clock, Hash } from 'lucide-react';

const emptyForm = { title: '', description: '', audio_url: '', external_url: '', cover_art_url: '', duration: '', season: '', episode_number: '', topics: '' };

export default function EpisodeManager() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchEpisodes(); }, []);

  const fetchEpisodes = async () => {
    try {
      const res = await fetch(`${API}/creative-episodes/me`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setItems(d.episodes || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        audio_url: form.audio_url.trim() || null,
        external_url: form.external_url.trim() || null,
        cover_art_url: form.cover_art_url.trim() || null,
        duration: form.duration ? parseInt(form.duration) : null,
        season: form.season ? parseInt(form.season) : null,
        episode_number: form.episode_number ? parseInt(form.episode_number) : null,
        topics: form.topics ? form.topics.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      const url = editingId ? `${API}/creative-episodes/${editingId}` : `${API}/creative-episodes`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Failed to save');
      toast.success(editingId ? 'Episode updated!' : 'Episode added!');
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchEpisodes();
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title || '',
      description: item.description || '',
      audio_url: item.audio_url || '',
      external_url: item.external_url || '',
      cover_art_url: item.cover_art_url || '',
      duration: item.duration ? String(item.duration) : '',
      season: item.season ? String(item.season) : '',
      episode_number: item.episode_number ? String(item.episode_number) : '',
      topics: (item.topics || []).join(', '),
    });
    setEditingId(item.episode_id);
    setShowForm(true);
  };

  const handleDelete = async (episodeId) => {
    if (!window.confirm('Delete this episode?')) return;
    try {
      const res = await fetch(`${API}/creative-episodes/${episodeId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Episode deleted');
      fetchEpisodes();
    } catch (err) { toast.error(err.message); }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return null;
    const s = parseInt(seconds);
    if (s <= 0) return null;
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="episode-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Mic2 className="w-5 h-5" />Your Episodes</h3>
        <Button size="sm" onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }} data-testid="add-episode-btn">
          <Plus className="w-4 h-4 mr-1" />Add Episode
        </Button>
      </div>

      {showForm && (
        <Card className="glass-card border-border p-5 mb-5" data-testid="episode-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">{editingId ? 'Edit Episode' : 'New Episode'}</h4>
            <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); setEditingId(null); }}><X className="w-4 h-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input placeholder="Episode Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="episode-title-input" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="episode-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Audio URL (MP3, Spotify, etc.)" value={form.audio_url} onChange={e => setForm(f => ({ ...f, audio_url: e.target.value }))} data-testid="episode-audio-input" />
              <Input placeholder="External Link (Apple Podcasts, etc.)" value={form.external_url} onChange={e => setForm(f => ({ ...f, external_url: e.target.value }))} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="Cover Art URL" value={form.cover_art_url} onChange={e => setForm(f => ({ ...f, cover_art_url: e.target.value }))} />
              <Input type="number" placeholder="Duration (seconds)" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} data-testid="episode-duration-input" />
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" placeholder="Season #" value={form.season} onChange={e => setForm(f => ({ ...f, season: e.target.value }))} />
                <Input type="number" placeholder="Ep #" value={form.episode_number} onChange={e => setForm(f => ({ ...f, episode_number: e.target.value }))} />
              </div>
            </div>
            <Input placeholder="Topics (comma separated)" value={form.topics} onChange={e => setForm(f => ({ ...f, topics: e.target.value }))} data-testid="episode-topics-input" />
            <Button type="submit" disabled={submitting} className="w-full" data-testid="save-episode-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Update Episode' : 'Add Episode'}
            </Button>
          </form>
        </Card>
      )}

      {items.length === 0 ? (
        <Card className="glass-card border-border p-8 text-center">
          <Mic2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No episodes yet. Share your content!</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(ep => (
            <Card key={ep.episode_id} className="glass-card border-border p-4 group" data-testid={`episode-manage-${ep.episode_id}`}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {ep.cover_art_url ? (
                    <img src={ep.cover_art_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Mic2 className="w-6 h-6 text-green-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-sm">{ep.title}</h4>
                    {ep.season && <Badge variant="outline" className="text-xs">S{ep.season}</Badge>}
                    {ep.episode_number && <Badge variant="outline" className="text-xs">E{ep.episode_number}</Badge>}
                  </div>
                  {ep.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ep.description}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    {ep.duration && (
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(ep.duration)}</span>
                    )}
                    {ep.topics?.length > 0 && (
                      <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{ep.topics.slice(0, 3).join(', ')}</span>
                    )}
                    {ep.external_url && (
                      <a href={ep.external_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-green-400 hover:underline" onClick={e => e.stopPropagation()}>
                        <ExternalLink className="w-3 h-3" />Link
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(ep)} data-testid={`edit-episode-${ep.episode_id}`}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(ep.episode_id)} data-testid={`delete-episode-${ep.episode_id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
